import React from "react";

export function PassDetails({
  pass = {
    id: "",
    user_id: "",
    visit_date: "",
    purpose: "",
    tot_person: 1,
    vehicle_num: "",
    arrival_time: "",
    duration: 2, // Default duration in hours
    valid_till: "",
    createdAt: "",
    updatedAt: ""
  },
  onClose = () => {},
  onPrint = () => {},
  onShare = () => {},
}) {
  const calculateValidTill = (visitDate, arrivalTime, duration) => {
    // Parse the visit date and arrival time
    const [hours, minutes] = arrivalTime.split(':').map(Number);
    const visitDateTime = new Date(visitDate);
    visitDateTime.setHours(hours, minutes, 0, 0);

    // Add duration in hours
    const validTill = new Date(visitDateTime.getTime() + (duration * 60 * 60 * 1000));
    return validTill;
  };

  const getStatusBadgeClass = (validTill) => {
    const baseClasses =
      "px-4 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-2";
    const now = new Date();
    const validTillDate = new Date(validTill);

    if (validTillDate < now) {
      return `${baseClasses} bg-red-100 text-red-700`;
    } else if (validTillDate.getTime() - now.getTime() < 30 * 60 * 1000) { // Less than 30 minutes
      return `${baseClasses} bg-yellow-100 text-yellow-700`;
    } else {
      return `${baseClasses} bg-green-100 text-green-700`;
    }
  };

  const getStatusText = (validTill) => {
    const now = new Date();
    const validTillDate = new Date(validTill);

    if (validTillDate < now) {
      return "Expired";
    } else if (validTillDate.getTime() - now.getTime() < 30 * 60 * 1000) {
      return "Expiring Soon";
    } else {
      return "Valid";
    }
  };

  // Calculate valid till time
  const validTillTime = calculateValidTill(pass.visit_date, pass.arrival_time, pass.duration || 2);

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');

    // Get the user data from localStorage
    const userData = JSON.parse(localStorage.getItem("user")) || {};

    // Create the print content
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Gate Pass #${pass.id}</title>
          <style>
            @media print {
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                max-width: 800px;
                margin: 0 auto;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 2px solid #000;
                padding-bottom: 20px;
              }
              .pass-title {
                font-size: 24px;
                font-weight: bold;
                margin-bottom: 10px;
              }
              .pass-id {
                font-size: 18px;
                color: #666;
              }
              .section {
                margin-bottom: 30px;
              }
              .section-title {
                font-size: 18px;
                font-weight: bold;
                margin-bottom: 15px;
                color: #333;
              }
              .info-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 20px;
              }
              .info-item {
                margin-bottom: 15px;
              }
              .info-label {
                font-weight: bold;
                color: #666;
                margin-bottom: 5px;
              }
              .info-value {
                font-size: 16px;
              }
              .status {
                display: inline-block;
                padding: 5px 15px;
                border-radius: 20px;
                font-weight: bold;
                margin-top: 10px;
              }
              .status.valid { background-color: #e6ffe6; color: #006600; }
              .status.expiring { background-color: #fff3e6; color: #cc7700; }
              .status.expired { background-color: #ffe6e6; color: #cc0000; }
              .footer {
                margin-top: 40px;
                text-align: center;
                font-size: 12px;
                color: #666;
              }
              .profile-image {
                width: 150px;
                height: 150px;
                object-fit: cover;
                border-radius: 10px;
                margin: 20px auto;
                display: block;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://www.ssgkoba.com/static/media/ssgk-logo.843fc1b445374b9b40d7.png" alt="Sainik School Ghorakhal" class="logo" />

            <div class="pass-title">Gate Pass Sainik School, Ghorakhal
</div>
            <div class="pass-id">#${pass.id}</div>
          </div>

          <div class="section">
            <img src="${process.env.REACT_APP_API_BASE_URL +'/get-image?objectKey='+ JSON.parse(localStorage.getItem("user"))?.profile_image||'https://www.gravatar.com/avatar/?d=mp'}" alt="Profile" className="w-full h-full object-cover rounded-2xl" style="width: 150px; height: 150px; border-radius: 10px; margin: 20px auto; display: block;"/>

            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Name</div>
                <div class="info-value">${userData.first_name} ${userData.last_name}</div>
              </div>
              <div class="info-item">
                <div class="info-label">School Number</div>
                <div class="info-value">${userData.school_num || 'N/A'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Address</div>
                <div class="info-value">${userData.address || 'N/A'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Status</div>
                <div class="status ${getStatusText(pass.valid_till).toLowerCase().replace(' ', '-')}">
                  ${getStatusText(pass.valid_till)}
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Vehicle Details</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Vehicle Number</div>
                <div class="info-value">${pass.vehicle_num}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Visit Schedule</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Visit Date</div>
                <div class="info-value">${new Date(pass.visit_date).toLocaleDateString()}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Arrival Time</div>
                <div class="info-value">${pass.arrival_time}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Valid Till</div>

                <div class="info-value">${new Date(pass.valid_till).toLocaleString()}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Additional Information</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Total Persons</div>
                <div class="info-value">${pass.tot_person} ${pass.tot_person === 1 ? 'Person' : 'People'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Purpose of Visit</div>
                <div class="info-value">${pass.purpose}</div>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>This is an official gate pass document. Please keep it with you during your visit.</p>
            <p>Generated on: ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `;

    // Write the content to the new window
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for images to load before printing
    printWindow.onload = function() {
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 font-inter">
            Pass Details #{pass.id}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              <img src={process.env.REACT_APP_API_BASE_URL +'/get-image?objectKey='+ JSON.parse(localStorage.getItem("user"))?.profile_image||'https://www.gravatar.com/avatar/?d=mp'} alt="Profile" className="w-full h-full object-cover rounded-2xl" style={{width: '250px', height: '250px', borderRadius: '10px', margin: '20px auto', display: 'block'}}/>
            </div>

            <div className="flex-1 space-y-6">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 font-inter mb-2">
                  Gate Pass #{pass.id}
                </h3>
                <p className="text-gray-500"> {JSON.parse(localStorage.getItem("user"))?.first_name} {JSON.parse(localStorage.getItem("user"))?.last_name}</p>
                <p className="text-gray-500"> <span className="font-bold">School Number:</span> {JSON.parse(localStorage.getItem("user"))?.school_num}</p>
                <p className="text-gray-500"> <span className="font-bold">Address:</span> {JSON.parse(localStorage.getItem("user"))?.address}</p>

              </div>

              <div>
                <span className={getStatusBadgeClass(pass.valid_till)}>
                  <i
                    className={`fas fa-${
                      getStatusText(pass.valid_till) === "Valid"
                        ? "check-circle"
                        : getStatusText(pass.valid_till) === "Expiring Soon"
                        ? "clock"
                        : "times-circle"
                    }`}
                  ></i>
                  {getStatusText(pass.valid_till)}
                </span>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors"
                >
                  <i className="fas fa-print"></i>
                  Print Pass
                </button>

              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-b border-gray-100 py-6">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Vehicle Details
              </h4>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i className="fas fa-car text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">
                      {pass.vehicle_num}
                    </p>
                    <p className="text-sm text-gray-500">Vehicle Number</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Visit Schedule
              </h4>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i className="fas fa-calendar text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">
                      {new Date(pass.visit_date).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500">Visit Date</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i className="fas fa-clock text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">
                      {pass.arrival_time}
                    </p>
                    <p className="text-sm text-gray-500">Arrival Time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Additional Information
              </h4>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i className="fas fa-users text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">
                      {pass.tot_person} {pass.tot_person === 1 ? 'Person' : 'People'}
                    </p>
                    <p className="text-sm text-gray-500">Total Persons</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i className="fas fa-bullseye text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">
                      {pass.purpose}
                    </p>
                    <p className="text-sm text-gray-500">Purpose of Visit</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i className="fas fa-hourglass-end text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">
                     {new Date(pass.valid_till).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">Valid Till duration of visit</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

